import { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { StarIcon } from '@/components/islamic/Ornaments';
import { Link, useNavigate } from 'react-router';
import { createPortal } from 'react-dom';
import { scroller } from '@/lib/scroll';
import { useSettings } from '@/lib/store';

export function haptic(ms = 8) {
  if (!useSettings.getState().settings.haptics) return;
  try { navigator.vibrate?.(ms); } catch {}
}

/** Back: to the previous screen, or to `fallback` when this screen was opened directly (a link, a notification). */
export function BackButton({ fallback = '/' }: { fallback?: string }) {
  const nav = useNavigate();
  const canGoBack = typeof history !== 'undefined' && (history.state?.idx ?? 0) > 0;
  return <IconButton label="Back" onClick={() => (canGoBack ? nav(-1) : nav(fallback))}><ChevronLeft size={20} /></IconButton>;
}

/** back: show a back button (the value is where it goes if there's no history). */
export function LargeTitle({ title, subtitle, right, ar, back }: { title: string; subtitle?: ReactNode; right?: ReactNode; ar?: string; back?: string }) {
  return (
    <>
    {back && <div className="px-3 pt-1"><BackButton fallback={back} /></div>}
    <header className="flex items-end justify-between gap-3 px-4 pb-3 pt-2">
      <div className="min-w-0">
        {subtitle && <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">{subtitle}</div>}
        <h1 className="flex items-baseline gap-3 truncate text-[32px] font-bold leading-tight tracking-tight">
          {title}
          {ar && <span className="font-kufi text-[22px] font-semibold text-gold" dir="rtl">{ar}</span>}
        </h1>
      </div>
      {right && <div className="flex shrink-0 items-center gap-2 pb-1">{right}</div>}
    </header>
    </>
  );
}

export function IconButton({ label, onClick, children, to, active }: { label: string; onClick?: () => void; children: ReactNode; to?: string; active?: boolean }) {
  const cls = `press grid h-10 w-10 place-items-center rounded-full ${active ? 'bg-accent text-accent-ink' : 'bg-fill text-ink'}`;
  if (to) return <Link to={to} aria-label={label} className={cls}>{children}</Link>;
  return <button type="button" aria-label={label} onClick={() => { haptic(); onClick?.(); }} className={cls}>{children}</button>;
}

export function Section({ title, children, footer, className = '' }: { title?: string; children: ReactNode; footer?: ReactNode; className?: string }) {
  return (
    <section className={`px-4 pb-6 ${className}`}>
      {title && <h2 className="flex items-center gap-1.5 px-4 pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted"><StarIcon size={11} strokeWidth={2} className="text-gold" />{title}</h2>}
      <div className="group">{children}</div>
      {footer && <p className="px-4 pt-2 text-[13px] leading-snug text-muted">{footer}</p>}
    </section>
  );
}

export function Row({ icon, label, detail, to, onClick, children, danger }: {
  icon?: ReactNode; label: ReactNode; detail?: ReactNode; to?: string; onClick?: () => void; children?: ReactNode; danger?: boolean;
}) {
  const inner = (
    <>
      {icon && <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-fill text-accent">{icon}</span>}
      <span className={`min-w-0 flex-1 text-[16px] ${danger ? 'text-danger' : ''}`}>{label}</span>
      {detail != null && <span className="max-w-[55%] truncate text-right text-[15px] text-muted">{detail}</span>}
      {children}
      {(to || onClick) && !children && <ChevronRight size={18} className="shrink-0 text-faint" />}
    </>
  );
  if (to) return <Link to={to} className="row press">{inner}</Link>;
  if (onClick) return <button type="button" onClick={() => { haptic(); onClick(); }} className="row press w-full text-left">{inner}</button>;
  return <div className="row">{inner}</div>;
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" onClick={() => { haptic(); onChange(!checked); }} />
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => { haptic(); onChange(o.value); }}>{o.label}</button>
      ))}
    </div>
  );
}

export function Stepper({ value, min, max, step = 1, onChange, format }: { value: number; min: number; max: number; step?: number; onChange: (v: number) => void; format?: (v: number) => string }) {
  const clamp = (v: number) => Math.max(min, Math.min(max, Math.round(v / step) * step));
  return (
    <div className="flex items-center gap-1 rounded-full bg-fill p-1">
      <button type="button" aria-label="Decrease" className="press h-8 w-9 rounded-full text-lg font-semibold" onClick={() => { haptic(); onChange(clamp(value - step)); }}>−</button>
      <span className="min-w-[44px] text-center text-[15px] font-semibold tabular-nums">{format ? format(value) : value}</span>
      <button type="button" aria-label="Increase" className="press h-8 w-9 rounded-full text-lg font-semibold" onClick={() => { haptic(); onChange(clamp(value + step)); }}>+</button>
    </div>
  );
}

/* A number you can type (numeric keypad on iPhone) or step with −/+. The
   draft is committed — and clamped into range — on Enter, blur or a step. */
export function NumberField({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (v: number) => void; label: string }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = (raw: string) => {
    const n = parseInt(raw, 10);
    const v = Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : value;
    setDraft(String(v));
    if (v !== value) onChange(v);
  };
  return (
    <div className="flex items-center gap-1 rounded-full bg-fill p-1">
      <button type="button" aria-label={`Decrease ${label}`} disabled={value <= min} className="press h-8 w-9 rounded-full text-lg font-semibold disabled:opacity-30" onClick={() => { haptic(); onChange(Math.max(min, value - 1)); }}>−</button>
      <input aria-label={label} inputMode="numeric" pattern="[0-9]*" enterKeyHint="done" value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { commit((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).blur(); } }}
        onFocus={(e) => e.target.select()}
        className="w-12 rounded-lg bg-surface py-1 text-center text-[16px] font-semibold tabular-nums outline-none focus:ring-2 focus:ring-accent" />
      <button type="button" aria-label={`Increase ${label}`} disabled={value >= max} className="press h-8 w-9 rounded-full text-lg font-semibold disabled:opacity-30" onClick={() => { haptic(); onChange(Math.min(max, value + 1)); }}>+</button>
    </div>
  );
}

/* A repeat count: 1…max, then ∞ ("until I stop", stored as 0). */
export function RepeatStepper({ value, max = 50, onChange, label }: { value: number; max?: number; onChange: (v: number) => void; label: string }) {
  const inc = () => onChange(value === 0 ? 0 : value >= max ? 0 : value + 1);
  const dec = () => onChange(value === 0 ? max : Math.max(1, value - 1));
  return (
    <div className="flex items-center gap-1 rounded-full bg-fill p-1" role="group" aria-label={label}>
      <button type="button" aria-label={`Fewer ${label}`} className="press h-8 w-9 rounded-full text-lg font-semibold disabled:opacity-30" disabled={value === 1} onClick={() => { haptic(); dec(); }}>−</button>
      <span className="min-w-[44px] text-center text-[15px] font-semibold tabular-nums">{value === 0 ? '∞' : `${value}×`}</span>
      <button type="button" aria-label={`More ${label}`} className="press h-8 w-9 rounded-full text-lg font-semibold disabled:opacity-30" disabled={value === 0} onClick={() => { haptic(); inc(); }}>+</button>
      <button type="button" aria-label={`${label}: until I stop`} aria-pressed={value === 0} onClick={() => { haptic(); onChange(value === 0 ? 1 : 0); }}
        className={`press ml-0.5 h-8 rounded-full px-2.5 text-[13px] font-bold ${value === 0 ? 'bg-accent text-accent-ink' : 'text-muted'}`}>∞</button>
    </div>
  );
}

export function Range({ value, min, max, step = 1, onChange, label }: { value: number; min: number; max: number; step?: number; onChange: (v: number) => void; label: string }) {
  const pct = ((value - min) / (max - min)) * 100;
  return <input aria-label={label} type="range" className="range" min={min} max={max} step={step} value={value} style={{ ['--pct' as string]: `${pct}%` }} onChange={(e) => onChange(Number(e.target.value))} />;
}

/* Page scroll lock while sheets are open — counted, not save-and-restore.
   Saving the previous value broke with stacked sheets (e.g. the tajweed key
   opened from the Arabic display sheet): the second sheet saved "hidden" as
   the original, and closing them in that order left the app unable to scroll. */
let scrollLocks = 0;
const applyLock = () => { scroller().style.overflow = scrollLocks > 0 ? 'hidden' : ''; };
function lockScroll() { scrollLocks++; applyLock(); }
function unlockScroll() { scrollLocks = Math.max(0, scrollLocks - 1); applyLock(); }
/** Safety net (called on navigation): if no sheet is actually on screen, the page must scroll. */
export function releaseStaleScrollLock() {
  if (!document.querySelector('.sheet')) { scrollLocks = 0; applyLock(); }
}

export function Sheet({ open, onClose, title, children, actions }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    lockScroll();
    return () => { window.removeEventListener('keydown', onKey); unlockScroll(); };
  }, [open, onClose]);
  if (!open) return null;
  /* Portalled to <body>: a sheet opened from inside a glass header/bar would
     otherwise be positioned against that element (backdrop-filter makes it the
     containing block for position: fixed) and open off-screen. */
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} className="sheet glass-sheet">
        <div className="grabber shrink-0" />
        {(title || actions) && (
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-2 pt-1">
            <h2 className="text-[19px] font-bold">{title}</h2>
            <div className="flex items-center gap-2">
              {actions}
              <button type="button" onClick={onClose} className="press rounded-full bg-fill px-3 py-1.5 text-[14px] font-semibold">Done</button>
            </div>
          </div>
        )}
        <div className="sheet-body pb-4">{children}</div>
      </div>
    </>,
    document.body,
  );
}

export function Ring({ value, size = 120, stroke = 12, children }: { value: number; size?: number; stroke?: number; children?: ReactNode }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="ring-track" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round" className="ring-val" strokeDasharray={c} strokeDashoffset={c * (1 - v)} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">{children}</div>
    </div>
  );
}

export function Card({ children, className = '', to, onClick }: { children: ReactNode; className?: string; to?: string; onClick?: () => void }) {
  const cls = `block rounded-[20px] bg-surface p-4 shadow-[inset_0_0_0_1px_var(--card-edge)] ${to || onClick ? 'press' : ''} ${className}`;
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={`${cls} w-full text-left`}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

export function Chip({ active, onClick, children }: { active?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={() => { haptic(); onClick(); }} aria-pressed={active}
      className={`press shrink-0 rounded-full px-3.5 py-1.5 text-[14px] font-semibold ${active ? 'bg-accent text-accent-ink' : 'bg-fill text-ink'}`}>
      {children}
    </button>
  );
}

export function Empty({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-8 py-12 text-center">
      {icon && <div className="mb-1 text-faint">{icon}</div>}
      <div className="text-[17px] font-semibold">{title}</div>
      {body && <p className="text-[15px] text-muted">{body}</p>}
      {action}
    </div>
  );
}

export const fmtDuration = (sec: number) => {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};
